import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  X,
  Plus,
  Trash2,
  Star,
  Loader2,
  Mail,
  Check,
  UserPlus,
} from "lucide-react";
import ShareContactDialog from "./ShareContactDialog";
import AddressAutocomplete from "../reuseable/AddressAutocomplete";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { normalizeContact } from "../../utils/contactNormalize";
import { updateContact } from "../../utils/contactsApi";
import {
  loadContactGroupsFromFirestore,
  createContactGroup,
  updateContactMemberships,
} from "../../utils/contactGroups";
import SendTemplateModal from "../email/SendTemplateModal";
import ReferredByPicker from "../lead-tracker/ReferredByPicker";
import { setContactReferrer, listReferredContacts } from "../../utils/firebasefunctions";

// Google-Contacts-style unified drawer. Click a contact → see every rich
// field + labels + linked deals, edit any field with auto-save on blur.
// Replaces the old basic contact-detail-drawer and the separate
// ContactEditDrawer (which is now create-only).
//
// Save semantics:
//   - Every text/select blur on a field sends the FULL form state to the
//     /update-contact Cloud Function (which syncs to Google + Firestore).
//   - Labels add/remove use /update-contact-memberships separately.
//   - A small "Saved" indicator appears next to the contact name briefly
//     after each successful save.

const PHONE_TYPES = ["mobile", "home", "work", "other"];
const EMAIL_TYPES = ["personal", "work", "other"];

const inputClass =
  "w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500";

function Section({ title, children }) {
  return (
    <section className="mb-5">
      <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

// Build the initials from a contact's name. Used when there's no photoUrl.
function initialsFor(contact) {
  const first = (contact?.firstName || "").trim();
  const last = (contact?.lastName || "").trim();
  const seed =
    (first[0] || "") + (last[0] || "") ||
    (contact?.name || "").trim()[0] ||
    "?";
  return seed.toUpperCase().slice(0, 2);
}

export default function ContactDrawer({
  contact: rawContact,
  deals,
  onClose,
  onContactUpdated,
}) {
  const [{ user }] = useStateValue();
  const [form, setForm] = useState(() => normalizeContact(rawContact));
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [sendTemplateOpen, setSendTemplateOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Track the latest form value in a ref so the debounced save handler
  // always sees the most recent state without re-binding.
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // If the parent swaps to a different contact, reset to the new normalized
  // shape. Keyed on the contact id so we don't churn on every parent render.
  useEffect(() => {
    setForm(normalizeContact(rawContact));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawContact?.id]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close on Esc.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Referrals: who referred this contact + everyone this contact referred.
  const [referred, setReferred] = useState([]);
  const [refPickerOpen, setRefPickerOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!form?.id) return undefined;
    listReferredContacts(form.id).then((list) => {
      if (!cancelled) setReferred(list);
    });
    return () => {
      cancelled = true;
    };
  }, [form?.id]);

  // Available labels for the picker (loaded once from Firestore).
  const [availableGroups, setAvailableGroups] = useState([]);
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [labelCreateName, setLabelCreateName] = useState("");
  const [creatingLabel, setCreatingLabel] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadContactGroupsFromFirestore(user?.id).then((groups) => {
      if (!cancelled) setAvailableGroups(groups);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // -------------------------------------------------------------------------
  // The single save path. Sends the full form state to /update-contact via
  // the shared helper, which writes to Google + Firestore in one call.
  // -------------------------------------------------------------------------
  const persist = async () => {
    if (!form?.id) return;
    setSaving(true);
    try {
      const result = await updateContact({
        user,
        contact: form,
        form: formRef.current,
        offlineFallback: false,
      });

      // Backend may have created the Google resource on first edit — capture it.
      if (result.googleResourceName && !form.googleResourceName) {
        setForm((prev) => ({
          ...prev,
          googleResourceName: result.googleResourceName,
        }));
      }

      onContactUpdated?.(form.id, result);

      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1200);
    } catch (err) {
      console.error("Auto-save failed:", err);
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Field helpers
  // -------------------------------------------------------------------------
  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setAddress = (key, value) =>
    setForm((prev) => ({
      ...prev,
      address: { ...prev.address, [key]: value },
    }));

  const setOrg = (key, value) =>
    setForm((prev) => ({
      ...prev,
      organization: { ...prev.organization, [key]: value },
    }));

  // -------- Phone helpers (mutating a list saves immediately, since the
  //          user's intent is committed once they click an action button) --
  const addPhone = () => {
    setForm((prev) => ({
      ...prev,
      phones: [
        ...prev.phones,
        { value: "", type: "mobile", primary: prev.phones.length === 0 },
      ],
    }));
  };
  const updatePhone = (i, patch) =>
    setForm((prev) => ({
      ...prev,
      phones: prev.phones.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
    }));
  const removePhone = (i) => {
    setForm((prev) => {
      const next = prev.phones.filter((_, idx) => idx !== i);
      if (next.length > 0 && !next.some((p) => p.primary)) {
        next[0] = { ...next[0], primary: true };
      }
      return { ...prev, phones: next };
    });
    // Schedule a save on the next tick once state has flushed.
    setTimeout(persist, 0);
  };
  const setPrimaryPhone = (i) => {
    setForm((prev) => ({
      ...prev,
      phones: prev.phones.map((p, idx) => ({ ...p, primary: idx === i })),
    }));
    setTimeout(persist, 0);
  };

  // -------- Email helpers (same pattern) -----------------------------------
  const addEmail = () => {
    setForm((prev) => ({
      ...prev,
      emails: [
        ...prev.emails,
        { value: "", type: "personal", primary: prev.emails.length === 0 },
      ],
    }));
  };
  const updateEmail = (i, patch) =>
    setForm((prev) => ({
      ...prev,
      emails: prev.emails.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    }));
  const removeEmail = (i) => {
    setForm((prev) => {
      const next = prev.emails.filter((_, idx) => idx !== i);
      if (next.length > 0 && !next.some((e) => e.primary)) {
        next[0] = { ...next[0], primary: true };
      }
      return { ...prev, emails: next };
    });
    setTimeout(persist, 0);
  };
  const setPrimaryEmail = (i) => {
    setForm((prev) => ({
      ...prev,
      emails: prev.emails.map((e, idx) => ({ ...e, primary: idx === i })),
    }));
    setTimeout(persist, 0);
  };

  // -------------------------------------------------------------------------
  // Labels — these use the dedicated memberships endpoint, not /update-contact.
  // -------------------------------------------------------------------------
  const labelIsAttached = (group) =>
    (form.labels || []).some(
      (l) => l.groupResourceName === group.resourceName,
    );

  const syncLabels = async (addRns, removeRns) => {
    if (!form.googleResourceName) {
      toast.info(
        "Save the contact to Google first by editing another field, then try labels again.",
      );
      return;
    }
    try {
      const r = await updateContactMemberships({
        userId: user?.id,
        resourceName: form.googleResourceName,
        contactId: form.id,
        addGroupResourceNames: addRns,
        removeGroupResourceNames: removeRns,
      });
      const nextLabels = r?.labels || [];
      setForm((prev) => ({ ...prev, labels: nextLabels }));
      onContactUpdated?.(form.id, { labels: nextLabels });
    } catch (err) {
      console.error("Label sync failed:", err);
      toast.error(err.message || "Failed to update labels");
    }
  };

  const attachLabel = (group) => {
    setLabelPickerOpen(false);
    syncLabels([group.resourceName], []);
  };

  const removeLabel = (resourceName) => {
    syncLabels([], [resourceName]);
  };

  const handleCreateLabel = async () => {
    const name = labelCreateName.trim();
    if (!name) return;
    setCreatingLabel(true);
    try {
      const created = await createContactGroup(user?.id, name);
      const next = {
        resourceName: created.resourceName,
        name: created.name,
        source: "user",
      };
      setAvailableGroups((prev) => [...prev, next]);
      setLabelCreateName("");
      attachLabel(next);
      toast.success("Label created");
    } catch (err) {
      console.error("Create label failed:", err);
      toast.error(err.message || "Failed to create label");
    } finally {
      setCreatingLabel(false);
    }
  };

  // -------------------------------------------------------------------------
  // Derived: deals linked to this contact (carried over from the old drawer).
  // -------------------------------------------------------------------------
  const linkedDeals = useMemo(() => {
    if (!form?.id) return [];
    return (deals || []).filter((d) => {
      const customers = d?.customers || [];
      return customers.some((c) => c === form.id || c?.id === form.id);
    });
  }, [deals, form?.id]);

  if (!form) return null;

  const fullName =
    [form.firstName, form.lastName].filter(Boolean).join(" ").trim() ||
    form.name ||
    "Contact";

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/40 z-[100]" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] sm:w-[560px] max-h-[88vh] z-[110] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 border-b border-slate-200 bg-slate-900 text-white">
          <div className="flex items-start gap-3 px-5 pt-5 pb-3">
            {form.photoUrl ? (
              <img
                src={form.photoUrl}
                alt=""
                className="w-12 h-12 rounded-full object-cover bg-slate-200 flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-slate-700 text-white flex items-center justify-center text-base font-semibold flex-shrink-0">
                {initialsFor(form)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold truncate">{fullName}</h2>
              {form.nickname && (
                <p className="text-xs text-slate-300 italic truncate">
                  “{form.nickname}”
                </p>
              )}
              <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-300 h-4">
                {saving && (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 size={11} className="animate-spin" />
                    Saving…
                  </span>
                )}
                {!saving && savedFlash && (
                  <span className="inline-flex items-center gap-1 text-emerald-300">
                    <Check size={11} />
                    Saved
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Label chips row. Filter out unresolved labels (memberships whose
              group wasn't in contactGroups when /getcontacts ran — backend
              falls back to the raw hex ID for the name). Showing raw IDs in
              the UI looks broken; hiding them is the lesser evil. Usually
              fixed by clicking Sync Contacts to re-resolve. */}
          <div className="px-5 pb-3 flex flex-wrap items-center gap-1.5">
            {(form.labels || [])
              .filter((l) => {
                if (!l?.name) return false;
                // Looks-like-a-hex-id heuristic: bare 10+ hex chars only.
                return !/^[0-9a-f]{10,}$/i.test(l.name);
              })
              .map((l) => (
              <span
                key={l.groupResourceName}
                className="inline-flex items-center gap-1 bg-white/15 text-white text-xs font-medium px-2 py-1 rounded-full"
              >
                {l.name}
                <button
                  type="button"
                  onClick={() => removeLabel(l.groupResourceName)}
                  className="hover:text-white/70"
                  aria-label={`Remove label ${l.name}`}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
            <div className="relative">
              <button
                type="button"
                onClick={() => setLabelPickerOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white/90 hover:text-white border border-dashed border-white/40 hover:border-white/70 rounded-full px-2 py-1"
              >
                <Plus size={11} />
                Add label
              </button>
              {labelPickerOpen && (
                <div className="absolute z-20 left-0 mt-1 w-64 border border-slate-200 rounded-md bg-white shadow-lg p-2 space-y-1 max-h-[240px] overflow-y-auto text-slate-900">
                  {availableGroups.length === 0 && (
                    <p className="text-xs text-slate-500 px-1 py-1">
                      No labels synced yet — create one below.
                    </p>
                  )}
                  {availableGroups
                    .filter((g) => !labelIsAttached(g))
                    .map((g) => (
                      <button
                        key={g.resourceName}
                        type="button"
                        onClick={() => attachLabel(g)}
                        className="w-full text-left px-2 py-1 text-sm rounded hover:bg-slate-50 flex items-center justify-between"
                      >
                        <span>{g.name}</span>
                        {g.source === "system" && (
                          <span className="text-[10px] text-slate-400 uppercase tracking-wider">
                            Google
                          </span>
                        )}
                      </button>
                    ))}
                  <div className="border-t border-slate-200 pt-2 mt-1 flex items-center gap-2">
                    <input
                      value={labelCreateName}
                      onChange={(e) => setLabelCreateName(e.target.value)}
                      placeholder="New label name"
                      className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateLabel();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleCreateLabel}
                      disabled={!labelCreateName.trim() || creatingLabel}
                      className="px-2 py-1 text-xs font-semibold rounded bg-slate-800 hover:bg-slate-900 text-white disabled:opacity-50"
                    >
                      {creatingLabel ? "…" : "Create"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action row */}
          <div className="px-5 pb-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSendTemplateOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-white/15 hover:bg-white/25 text-white"
            >
              <Mail size={13} />
              Send template
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-white/15 hover:bg-white/25 text-white"
            >
              <UserPlus size={13} />
              Share
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <Section title="Name">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First Name">
                <input
                  className={inputClass}
                  value={form.firstName || ""}
                  onChange={(e) => setField("firstName", e.target.value)}
                  onBlur={persist}
                />
              </Field>
              <Field label="Last Name">
                <input
                  className={inputClass}
                  value={form.lastName || ""}
                  onChange={(e) => setField("lastName", e.target.value)}
                  onBlur={persist}
                />
              </Field>
            </div>
            <Field label="Nickname">
              <input
                className={inputClass}
                value={form.nickname || ""}
                onChange={(e) => setField("nickname", e.target.value)}
                onBlur={persist}
              />
            </Field>
          </Section>

          <Section title="Phone">
            {form.phones.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  className="border border-slate-300 rounded-md px-2 py-2 text-xs capitalize bg-white"
                  value={p.type}
                  onChange={(e) => updatePhone(i, { type: e.target.value })}
                  onBlur={persist}
                >
                  {PHONE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <input
                  className={`${inputClass} flex-1`}
                  type="tel"
                  value={p.value}
                  onChange={(e) => updatePhone(i, { value: e.target.value })}
                  onBlur={persist}
                  placeholder="Phone number"
                />
                <button
                  type="button"
                  onClick={() => setPrimaryPhone(i)}
                  title={p.primary ? "Primary" : "Make primary"}
                  className={`p-1.5 rounded ${
                    p.primary
                      ? "text-amber-500"
                      : "text-slate-300 hover:text-slate-500"
                  }`}
                >
                  <Star size={16} fill={p.primary ? "currentColor" : "none"} />
                </button>
                <button
                  type="button"
                  onClick={() => removePhone(i)}
                  className="p-1.5 rounded text-slate-400 hover:text-red-500"
                  aria-label="Remove phone"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPhone}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-800 hover:text-slate-900"
            >
              <Plus size={12} />
              Add phone
            </button>
          </Section>

          <Section title="Email">
            {form.emails.map((em, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  className="border border-slate-300 rounded-md px-2 py-2 text-xs capitalize bg-white"
                  value={em.type}
                  onChange={(e) => updateEmail(i, { type: e.target.value })}
                  onBlur={persist}
                >
                  {EMAIL_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <input
                  className={`${inputClass} flex-1`}
                  type="email"
                  value={em.value}
                  onChange={(e) => updateEmail(i, { value: e.target.value })}
                  onBlur={persist}
                  placeholder="email@example.com"
                />
                <button
                  type="button"
                  onClick={() => setPrimaryEmail(i)}
                  title={em.primary ? "Primary" : "Make primary"}
                  className={`p-1.5 rounded ${
                    em.primary
                      ? "text-amber-500"
                      : "text-slate-300 hover:text-slate-500"
                  }`}
                >
                  <Star size={16} fill={em.primary ? "currentColor" : "none"} />
                </button>
                <button
                  type="button"
                  onClick={() => removeEmail(i)}
                  className="p-1.5 rounded text-slate-400 hover:text-red-500"
                  aria-label="Remove email"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addEmail}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-800 hover:text-slate-900"
            >
              <Plus size={12} />
              Add email
            </button>
          </Section>

          <Section title="Address">
            <Field label="Street">
              {/* Picking a Google suggestion fills street, city, state and
                  ZIP in one go, then saves like any other field. */}
              <AddressAutocomplete
                className={inputClass}
                value={form.address?.street || ""}
                onChange={(v) => setAddress("street", v)}
                onPlaceParts={(parts) => {
                  setForm((prev) => {
                    const next = {
                      ...prev,
                      address: {
                        ...prev.address,
                        street: parts.street || prev.address?.street || "",
                        city: parts.city || prev.address?.city || "",
                        state: parts.state || prev.address?.state || "",
                        postalCode:
                          parts.postalCode || prev.address?.postalCode || "",
                      },
                    };
                    formRef.current = next;
                    return next;
                  });
                  persist();
                }}
                onBlur={persist}
              />
            </Field>
            <div className="grid grid-cols-6 gap-2">
              <div className="col-span-3">
                <Field label="City">
                  <input
                    className={inputClass}
                    value={form.address?.city || ""}
                    onChange={(e) => setAddress("city", e.target.value)}
                    onBlur={persist}
                  />
                </Field>
              </div>
              <div className="col-span-1">
                <Field label="State">
                  <input
                    className={inputClass}
                    value={form.address?.state || ""}
                    onChange={(e) => setAddress("state", e.target.value)}
                    onBlur={persist}
                  />
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="ZIP">
                  <input
                    className={inputClass}
                    value={form.address?.postalCode || ""}
                    onChange={(e) => setAddress("postalCode", e.target.value)}
                    onBlur={persist}
                  />
                </Field>
              </div>
            </div>
          </Section>

          <Section title="Organization">
            <Field label="Company">
              <input
                className={inputClass}
                value={form.organization?.company || ""}
                onChange={(e) => setOrg("company", e.target.value)}
                onBlur={persist}
              />
            </Field>
            <Field label="Title">
              <input
                className={inputClass}
                value={form.organization?.title || ""}
                onChange={(e) => setOrg("title", e.target.value)}
                onBlur={persist}
              />
            </Field>
          </Section>

          <Section title="Personal">
            <Field label="Birthday">
              <input
                className={inputClass}
                type="date"
                value={form.birthday || ""}
                onChange={(e) => setField("birthday", e.target.value)}
                onBlur={persist}
              />
            </Field>
            <Field label="Home Anniversary">
              <input
                className={inputClass}
                type="date"
                value={form.homeAnniversary || ""}
                onChange={(e) => setField("homeAnniversary", e.target.value)}
                onBlur={persist}
              />
            </Field>
            <Field label="Spouse / Partner">
              <input
                className={inputClass}
                value={form.spouse || ""}
                onChange={(e) => setField("spouse", e.target.value)}
                onBlur={persist}
                placeholder="Name"
              />
            </Field>
          </Section>

          <Section title="Notes">
            <textarea
              className={`${inputClass} min-h-[120px] resize-y`}
              value={form.notes || ""}
              onChange={(e) => setField("notes", e.target.value)}
              onBlur={persist}
              placeholder="Anything worth remembering about this contact…"
            />
          </Section>

          <Section title={`Linked Deals (${linkedDeals.length})`}>
            {linkedDeals.length === 0 ? (
              <p className="text-sm text-slate-400 italic">
                No deals linked to this contact.
              </p>
            ) : (
              <ul className="space-y-2">
                {linkedDeals.map((deal) => (
                  <li key={deal.createdat || deal.id}>
                    <Link
                      to={`/dashboard/deals-detail/${deal.createdat}`}
                      onClick={onClose}
                      className="block px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition no-underline"
                    >
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {deal?.propertyaddress || "Untitled deal"}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                        <span>{deal?.dealtype || "Deal"}</span>
                        {deal?.dealStage && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span>{deal.dealStage}</span>
                          </>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Referrals">
            <div className="flex items-center justify-between text-sm py-1">
              <span className="text-slate-500">Referred by</span>
              <span className="flex items-center gap-2">
                {form.referredByName || <span className="text-slate-400">None</span>}
                <button
                  type="button"
                  className="text-xs text-linkColor hover:underline"
                  onClick={() => setRefPickerOpen(true)}
                >
                  {form.referredByName ? "Change" : "Set"}
                </button>
                {form.referredByName && (
                  <button
                    type="button"
                    className="text-xs text-slate-400 hover:underline"
                    onClick={async () => {
                      const r = await setContactReferrer(form.id, null);
                      if (r.success) {
                        setForm({ ...form, referredByContactId: "", referredByName: "" });
                        onContactUpdated?.(form.id, { referredByContactId: "", referredByName: "" });
                      }
                    }}
                  >
                    Clear
                  </button>
                )}
              </span>
            </div>
            <div className="text-sm py-1">
              <div className="text-slate-500 mb-1">
                Referred ({referred.length})
              </div>
              {referred.length === 0 ? (
                <div className="text-slate-400 text-xs">
                  No referrals from this contact yet
                </div>
              ) : (
                referred.map((c) => (
                  <div key={c.id} className="text-slate-800 py-0.5">
                    {c.name || [c.firstName, c.lastName].filter(Boolean).join(" ") || "Unnamed contact"}
                  </div>
                ))
              )}
            </div>
            {refPickerOpen && (
              <ReferredByPicker
                excludeContactId={form.id}
                onClose={() => setRefPickerOpen(false)}
                onDone={async (ref) => {
                  setRefPickerOpen(false);
                  const r = await setContactReferrer(form.id, ref);
                  if (r.success) {
                    setForm({ ...form, referredByContactId: ref.contactId, referredByName: ref.name });
                    onContactUpdated?.(form.id, { referredByContactId: ref.contactId, referredByName: ref.name });
                  }
                }}
              />
            )}
          </Section>
        </div>
      </div>

      {sendTemplateOpen && (
        <SendTemplateModal
          contact={form}
          onClose={() => setSendTemplateOpen(false)}
        />
      )}
      {shareOpen && (
        <ShareContactDialog
          contact={form}
          onClose={() => setShareOpen(false)}
        />
      )}
    </>,
    document.body,
  );
}
